import { flatten, isEmpty} from 'lodash'
import {
  BANNER_IMAGE_MODULE, EmailAttributes, RASA_IMAGE_API_ENDPOINT, LEFT_SQUARE,
  RasaEmailFooterImages, RIGHT_SQUARE, SQUARE_IMAGE_MODULE
} from './constants'
import { TemplateModule } from './types/email_layout'

const BLACK = 'black'
const WHITE = 'white'
const WHITE_COLORS = [
  WHITE,
  'ffffff',
]
const DARK_COLORS = [
  BLACK,
  '000000',
]

export const convertHexToRGB = (hex: string): number[] => {
  const aRgbHex = hex.slice(1).match(/.{1,2}/g)
  const aRgb = [
      parseInt(aRgbHex[0], 16),
      parseInt(aRgbHex[1], 16),
      parseInt(aRgbHex[2], 16),
  ]
  return aRgb
}

export const deltaE = (rgbA, rgbB) => {
  const labA = rgb2lab(rgbA)
  const labB = rgb2lab(rgbB)
  const deltaL = labA[0] - labB[0]
  const deltaA = labA[1] - labB[1]
  const deltaB = labA[2] - labB[2]
  const c1 = Math.sqrt(labA[1] * labA[1] + labA[2] * labA[2])
  const c2 = Math.sqrt(labB[1] * labB[1] + labB[2] * labB[2])
  const deltaC = c1 - c2
  let deltaH = deltaA * deltaA + deltaB * deltaB - deltaC * deltaC
  deltaH = deltaH < 0 ? 0 : Math.sqrt(deltaH)
  const sc = 1.0 + 0.045 * c1
  const sh = 1.0 + 0.015 * c1
  const deltaLKlsl = deltaL / (1.0)
  const deltaCkcsc = deltaC / (sc)
  const deltaHkhsh = deltaH / (sh)
  const i = deltaLKlsl * deltaLKlsl + deltaCkcsc * deltaCkcsc + deltaHkhsh * deltaHkhsh
  return i < 0 ? 0 : Math.sqrt(i)
}

export const rgb2lab = (rgb) => {
  let r = rgb[0] / 255
  let g = rgb[1] / 255
  let b = rgb[2] / 255
  let x = 0
  let y = 0
  let z = 0

  r = (r > 0.04045) ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92
  g = (g > 0.04045) ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92
  b = (b > 0.04045) ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92
  x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047
  y = (r * 0.2126 + g * 0.7152 + b * 0.0722) / 1.00000
  z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883
  x = (x > 0.008856) ? Math.pow(x, 1 / 3) : (7.787 * x) + 16 / 116
  y = (y > 0.008856) ? Math.pow(y, 1 / 3) : (7.787 * y) + 16 / 116
  z = (z > 0.008856) ? Math.pow(z, 1 / 3) : (7.787 * z) + 16 / 116
  return [(116 * y) - 16, 500 * (x - y), 200 * (y - z)]
}

export const areCloseColors = (color1: string, color2: string): boolean => {
  if (color1 && color2) {
    const deltaEValue = deltaE(convertHexToRGB(color1), convertHexToRGB(color2))
    return deltaEValue < 35
  } else {
    return false
  }

}

const isLightFooter = (props: any): boolean => {
  const color = getEmailAttribute(props, EmailAttributes.primaryColor)
  return color && (WHITE_COLORS.includes(color.toLowerCase().slice(1)) || areCloseColors(color , '#ffffff'))
}
const isLightFooterFont = (props: any): boolean => {
  const color = getEmailAttribute(props, EmailAttributes.footerFontColor)
  return color && WHITE_COLORS.includes(color.toLowerCase().slice(1) || areCloseColors(color , '#ffffff'))
}

const isDarkFooter = (props: any): boolean => {
  const color = getEmailAttribute(props, EmailAttributes.primaryColor)
  return color && DARK_COLORS.includes(color.toLowerCase().slice(1) || areCloseColors(color , '#000000'))
}
const isDarkFooterFont = (props: any): boolean => {
  const color = getEmailAttribute(props, EmailAttributes.footerFontColor)
  return color && DARK_COLORS.includes(color.toLowerCase().slice(1) || areCloseColors(color , '#000000'))
}

const footerFontColor = (props: any): string => {
  const color = getEmailAttribute(props, EmailAttributes.footerFontColor)
  if ( isEmpty(color) ) {
    return isLightFooter(props) ? BLACK : WHITE
  } else {
    return (isDarkFooter(props) && isDarkFooterFont(props)) ? WHITE
      : (isLightFooter(props) && isLightFooterFont(props)) ? BLACK
      : areCloseColors(color, getEmailAttribute(props, EmailAttributes.primaryColor)) ?
        (isLightFooter(props) ? BLACK : WHITE)
        : color
  }
}

export const getEmailAttribute = (props: any, key: EmailAttributes) => props[key]

export const footerStyles = (props: any) => ({
  backgroundColor: getEmailAttribute(props, EmailAttributes.primaryColor),
  color: footerFontColor(props),
  fontFamily: getEmailAttribute(props, EmailAttributes.newsletterFontFamily),
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const footerLogo = (props: any): RasaEmailFooterImages => {
  return RasaEmailFooterImages.large
}

export interface SocialMediaIconOptions {
  requiresBlack?: boolean,
}
export const socialMediaIcon = (name: string, props: any, options: SocialMediaIconOptions = {}): string => {
  const color = options.requiresBlack || isLightFooter(props.session) ? 'black' : 'white'
  return `${RASA_IMAGE_API_ENDPOINT}/hosted-images-newsbrief/path/social-media/${name}_64_${color}_square.png`
}

export enum CapTextStrategy {
  // Important that default also be 0 - so "not specified" is same as "0" as "falsey" values
  // And these should be strings - since that's what the underlying properties are
  SENTENCE_CAP = '0',
  EXACT_CAP = '1',
  WORD_CAP = '2',
  NO_CAP = '3',
}
interface CappedText {
  isCapped: boolean,
  text: string,
}
export const capText = (text: string, maxLength: number, capStrategy: CapTextStrategy): CappedText => {
  const trimmedText: string = text ? text.trim() : ''
  switch (capStrategy) {
    case CapTextStrategy.EXACT_CAP:
      return {
        isCapped: trimmedText.length > maxLength,
        text: trimmedText.slice(0, maxLength),
      }
    case CapTextStrategy.WORD_CAP:
    case CapTextStrategy.SENTENCE_CAP:
      // Word and Sentence are implemented identically on the client right now
      if (trimmedText.length > maxLength) {
        const cappedText: string = trimmedText.slice(0, maxLength)
        const lastSpace: number = cappedText.lastIndexOf(' ')
        if (lastSpace > 0) {
          return {
            isCapped: true,
            text: trimmedText.slice(0, lastSpace).trim(),
          }
        } else {
          // no space - I guess return the trimmed text
          return {
            isCapped: true,
            text: trimmedText.slice(0, maxLength),
          }
        }
      }
    case CapTextStrategy.NO_CAP:
    default:
      return {
        isCapped: false,
        text,
      }
  }
}

interface ContentTypeIdObj {
  contentType: string
  id: string
}

export const parseTypeId = (typeId: string): ContentTypeIdObj => {
  return {
    contentType: typeId.split('-')[0],
    id: typeId.split('-')[1],
  }
}

export const combineTypeId = (contentType: string, id: number): string => {
  return `${contentType}-${id}`
}

export const needsImageUpload = (imageUrl: string): boolean => {
    if (imageUrl.includes('rasa-hosted-images') || imageUrl.includes('rasa-self-service-logos')) {
      return false
    } else {
      return true
    }
}

export const getImageDisplayName = (
  moduleType: string, sequence: number, location: string = null): string => {

  if (moduleType === BANNER_IMAGE_MODULE) {
    return getBannerImageName(sequence)
  } else if (moduleType === SQUARE_IMAGE_MODULE) {
    return getSquareImageName(sequence, location)
  } else {
    return null
  }
}

export const getBannerImageName = (sequence: number): string => {
  return `Banner ${sequence}`
}

export const getSquareImageIndex = (sequence, location: string): number => {
  // 1 -> 1,2
  // 2 -> 3,4
  // 3 -> 5,6
  if (location === LEFT_SQUARE) {
    return (sequence * 2) - 1
  } else if (location === RIGHT_SQUARE) {
    return sequence * 2
  } else {
    return 0
  }
}

export const getSquareImageName = (sequence: number, location: string): string => {
  return `Square ${getSquareImageIndex(sequence, location)}`
}

export const getSponsoredName = (sequence: number): string => {
  return `Sponsored ${sequence}`
}

export const getStaticImage = (
  staticImages: any,
  moduleType: string,
  sequence: number,
  location: string = null,
): any => {
  if (staticImages) {
    if (moduleType === BANNER_IMAGE_MODULE) {
      return getBannerImage(staticImages, sequence)
    } else if (moduleType === SQUARE_IMAGE_MODULE) {
      return getSquareStaticImage(staticImages, sequence, location)
    } else {
      return {}
    }
  } else {
    return {}
  }
}

export const getBannerImage = (allStaticImages: any[], sequence: number) => {
  const bannerImageName = getBannerImageName(sequence)
  return findStaticImage(allStaticImages, bannerImageName)
}

export const getSquareStaticImage = (allStaticImages: any[], sequence: number, location: string) => {
  const squareImageName = getSquareImageName(sequence, location)
  return findStaticImage(allStaticImages, squareImageName)
}

const findStaticImage = (allStaticImages: any[], typeName: string) => {
  if (Array.isArray(allStaticImages)) {
    return allStaticImages.find((x) => x.name === typeName) || {}
  } else {
    return Object.keys(allStaticImages)
                 .map((key) => allStaticImages[key])
                 .find((x: any) => x.name === typeName) || {}
  }

}

export const isCustomTemplate = (emailTemplate: string): boolean => {
  emailTemplate = emailTemplate || ''
  if (emailTemplate.indexOf('custom:') > -1) {
    return true
  }
  return false
}

export interface LocationOption {
  key: string,
  value: string,
}

// We may call an sp to get this value but, this is static data so keeping as constant for now.
export const getAllImageLocationsOptions = (): Promise<LocationOption[]> => {
  // limit to Banner = 7
  // limit Square = 14 i.e ( 7 * 2)
  const BANNER_IMAGE_COUNT = 7
  const SQUARE_IMAGE_COUNT = 7
  const bannerImageLocationOptions = [...Array(BANNER_IMAGE_COUNT).keys()].map((i) =>
    ([{key: getBannerImageName(i + 1), value: getBannerImageName(i + 1)}]))
  const squareImageLocationOptions = [...Array(SQUARE_IMAGE_COUNT).keys()].map((i) => {
    const leftImageName = getSquareImageName(i + 1, LEFT_SQUARE)
    const rightImageName = getSquareImageName(i + 1, RIGHT_SQUARE)
    return [
      {
        key: leftImageName,
        value: leftImageName,
      },
      {
        key: rightImageName,
        value: rightImageName,
      },
    ]
  })
  return Promise.resolve(flatten(bannerImageLocationOptions.concat(squareImageLocationOptions)))
}

export const getAllTextLocationsOptions = (): Promise<LocationOption[]> => {
  // limit to 7 Text
  const TEXT_COUNT = 7
  return Promise.resolve([...Array(TEXT_COUNT).keys()].map((i) => ({key: getTextName(i + 1), value: `Text${i + 1}`})))
}

export const getImageLocationOptions = (emailLayout: any): Promise<LocationOption[]> => {
  // Wrap in a promise if we ever go to db to get all locations
  const templateModules: TemplateModule[] = emailLayout.template_modules
  if (templateModules && !isCustomTemplate(emailLayout.email_template)) {
    const imageModules = templateModules.filter((tm) => tm.type.toLocaleLowerCase().includes('image'))
    const imageLocationOptions = imageModules.map((im) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      if (im.type === 'image') {
        const bannerName = getBannerImageName(im.sequence)
        return {
          key: bannerName,
          value: bannerName,
        }
      } else {
        const leftImageName = getSquareImageName(im.sequence, LEFT_SQUARE)
        const rightImageName = getSquareImageName(im.sequence, RIGHT_SQUARE)
        return [
          {
            key: leftImageName,
            value: leftImageName,
          },
          {
            key: rightImageName,
            value: rightImageName,
          },
        ]
      }
    })
    const sortedImageLocationOptions = flatten(imageLocationOptions).sort((a, b) => {
      if (a.value < b.value) {
        return -1
      }
      if (a.value > b.value) {
        return 1
      }
      return 0
    })
    return Promise.resolve(sortedImageLocationOptions)
  } else {
    return getAllImageLocationsOptions()
  }
}

export const getTextName = (sequence: number): string => `Text ${sequence}`

export const getTextLocationOptions = (emailLayout: any): Promise<LocationOption[]> => {
  const templateModules: TemplateModule[] = emailLayout.template_modules
  if (templateModules && !isCustomTemplate(emailLayout.email_template)) {
    const textModules = templateModules.filter((tm) => tm.type.toLocaleLowerCase().includes('text'))
    const textLocationOptions = textModules.map((im) => ({
      key: getTextName(im.sequence),
      value: `Text${im.sequence}`,
    }))
    const sortedTextLocationOptions = textLocationOptions.sort((a, b) => {
      if (a.value < b.value) {
        return -1
      }
      if (a.value > b.value) {
        return 1
      }
      return 0
    })
    return Promise.resolve(sortedTextLocationOptions)
  } else {
    return getAllTextLocationsOptions()
  }

}
